import React from 'react';
import UtilisateurError from '../../../../enums/UtilisateurError';
import type { Bureau } from '../../../../interfaces/bureaux';
import type Collectivite from '../../../../interfaces/collectivite';
import type { UtilisateurCollectiviteToCreate } from '../../../../interfaces/utilisateurCollectivite';
import CollectivitesSegment from '../CollectivitesSegment';

interface ParapheurStepProps {
	entitesPastell: Collectivite[];
	newUser: Partial<UtilisateurCollectiviteToCreate>;
	setNewUser: (user: Partial<UtilisateurCollectiviteToCreate>) => void;
	fieldsOnError: UtilisateurError[];
	bureaux: Record<string, Bureau[]>;
	setBureaux: (bureaux: Record<string, Bureau[]>) => void;
	isBureauxLoading: boolean;
}

const ParapheurStep = ({
	entitesPastell,
	newUser,
	setNewUser,
	fieldsOnError,
	bureaux,
	setBureaux,
	isBureauxLoading,
}: ParapheurStepProps) => {
	const handleSelectBureau = (bureau: Bureau) => {
		// Mettre à jour les bureaux pour l'entité concernée
		const updatedBureauxForEntite = bureaux[bureau.id_e].map((b) => {
			if (b.id === bureau.id) {
				return { ...b, isSelected: !b.isSelected };
			}
			return b;
		});

		// Mettre à jour l'objet bureaux avec les bureaux modifiés pour cette entité
		const updatedBureaux: Record<string, Bureau[]> = {
			...bureaux,
			[bureau.id_e]: updatedBureauxForEntite,
		};
		setBureaux(updatedBureaux);

		// Trouver le bureau mis à jour pour connaître son nouvel état
		const updatedBureau = updatedBureauxForEntite.find(
			(b) => b.id === bureau.id,
		);

		// Mettre à jour les bureaux dans newUser.collectivites
		const updatedCollectivites = newUser.collectivites?.map((collectivite) => {
			if (collectivite.id_e === bureau.id_e) {
				const bureauExists = collectivite.bureaux?.some(
					(b) => b.id === bureau.id,
				);

				if (updatedBureau?.isSelected) {
					// Si le bureau est sélectionné et n'est pas déjà dans la liste, on l'ajoute
					if (!bureauExists) {
						return {
							...collectivite,
							bureaux: [...(collectivite.bureaux || []), updatedBureau],
						};
					}
				} else {
					// Si le bureau est décoché, on le retire
					return {
						...collectivite,
						bureaux:
							collectivite.bureaux?.filter((b) => b.id !== bureau.id) || [],
					};
				}
			}

			return collectivite;
		});

		setNewUser({
			...newUser,
			collectivites: updatedCollectivites,
		});
	};

	const handleSelectCollectivite = (idE: string, checked: boolean) => {
		if (checked) {
			// Ajouter la collectivité si elle n'existe pas déjà
			const collectiviteExists = newUser.collectivites?.some(
				(c) => c.id_e === idE,
			);

			if (!collectiviteExists) {
				const collectiviteToAdd = entitesPastell.find((c) => c.id_e === idE);
				if (collectiviteToAdd) {
					const { collectiviteSlowId, collectiviteSlowName, ...rest } =
						collectiviteToAdd;
					setNewUser({
						...newUser,
						collectivites: [
							...(newUser.collectivites || []),
							{ ...rest, roles: [] },
						],
					});
				}
			}
		} else {
			// Retirer la collectivité
			const updatedCollectivites = newUser.collectivites?.filter(
				(c) => c.id_e !== idE,
			);
			setNewUser({
				...newUser,
				collectivites: updatedCollectivites,
			});
		}
	};

	return (
		<CollectivitesSegment
			bureaux={bureaux}
			collectivites={entitesPastell}
			isBureauxLoading={isBureauxLoading}
			onSelectBureau={handleSelectBureau}
			onSelectCollectivite={handleSelectCollectivite}
			newUserCollectivites={newUser.collectivites || []}
			error={fieldsOnError.includes(UtilisateurError.COLLECTIVITE)}
		/>
	);
};

export default ParapheurStep;
